import { Component, Input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Organisation } from '../common/organisation';
import { ContextGraphService, ContextRequest, ContextSource } from './context-graph.service';
import { AnalysisScope } from './scope';

// A small node-and-edge icon beside something an analysis view points at (a role, an action, an output, a detour, a
// recommendation). Clicking it shows that thing's context as a graph, with a way into the view that explains it
@Component({
    selector: 'app-context-graph-button',
    standalone: true,
    imports: [MatTooltipModule],
    template: `
        <button type="button" class="context-button" [matTooltip]="tooltip" [attr.aria-label]="tooltip" (click)="open($event)" data-testid="context-graph-button">
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path d="M5 5 L15 8 M5 5 L8 15 M15 8 L8 15" />
                <circle cx="5" cy="5" r="2.6" class="focus" />
                <circle cx="15" cy="8" r="2.2" />
                <circle cx="8" cy="15" r="2.2" />
            </svg>
        </button>
    `,
    styles: [`
        :host { display: inline-flex; vertical-align: middle; }
        .context-button { display: inline-grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 1px solid transparent; border-radius: 7px;
            background: transparent; color: var(--slate, #5b6172); cursor: pointer; transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease; }
        .context-button:hover, .context-button:focus-visible { background: var(--teal-soft, #e0f5f4); border-color: var(--teal, #00a9a5); color: var(--teal, #00a9a5); outline: none; }
        path { fill: none; stroke: currentColor; stroke-width: 1.4; stroke-linecap: round; }
        circle { fill: var(--paper, #fff); stroke: currentColor; stroke-width: 1.5; }
        circle.focus { fill: currentColor; }
    `],
})
export class ContextGraphButtonComponent {
    @Input({ required: true }) organisation!: Organisation;
    @Input({ required: true }) scope!: AnalysisScope;
    @Input({ required: true }) request!: ContextRequest;
    @Input() name?: string;                 // what it's about, for the tooltip and the dialog
    @Input() source?: ContextSource;        // the graph the page already has, so nothing needs loading

    constructor(private contextGraph: ContextGraphService) {}

    get tooltip() {
        return `Show ${this.name ? `the context of ${this.name}` : 'the context'} as a graph`;
    }

    open(event: Event) {
        // it often sits in a row that opens something else when clicked
        event.stopPropagation();
        if (this.organisation && this.scope && this.request) this.contextGraph.open(this.organisation, this.scope, this.request, this.name, this.source);
    }
}
